import Pointer from "@assets/svg/pointer.svg?react";
import IncProfit from "@assets/svg/incProfit.svg?react";
import Communication from "@assets/svg/communication.svg?react";
import Card from "./Card";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import {
  Container,
  TitleContainer,
  MainTitle,
  SubTitle,
  MainSection,
  AppHeaderBox,
  AppLogo,
  AppInfoContainer,
  AppName,
  AppDescription,
  ButtonContainer,
  PrimaryButton,
  SecondaryButton,
  CustomerServiceLink,
  ArrowIcon,
  BenefitsSection,
  BenefitsTextContainer,
  BenefitsContent,
  BenefitsTitle,
  BenefitsDescription,
  IconWrapper,
  BenefitTitle,
  BenefitDescription,
  BenefitImage,
  RelatedAppsBox,
  RelatedAppsHeader,
  RelatedAppsTitle,
  RelatedAppsSubtitle,
  RelatedAppsGrid,
} from "../styles/AppDetails";
import { useAppMarket } from "../../../services/useAppMarket";
import { useState } from "react";
import BenefitsSectionSkeleton from "./Skeleton";
import AppHeaderSkeleton from "./AppHeaderSkeleton";

type StatusKey = 1 | 2 | 3;
const statusValue: Record<StatusKey, string> = {
  1: "Disable",
  2: "ComingSoon",
  3: "Active",
};
const AppDetails = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const { id } = useParams();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const isRtl = language === "ar";
  // -----------------------------------
  const { GetMarketPlaceByIdQuery, GetAllMarketPlaceQuery } = useAppMarket();
  const { data: apps, isLoading: isLoadingApps } =
    GetAllMarketPlaceQuery(searchQuery);
  const { data: app, isLoading } = GetMarketPlaceByIdQuery({ id: id });
  const name = isRtl ? app?.result?.nameAr : app?.result?.nameEn;
  const description = isRtl
    ? app?.result?.descriptionAr
    : app?.result?.descriptionEn;
  const logoUrl = app?.result?.logoUrl;
  const largLogoUrl = app?.result?.largLogoUrl;

  const benefitTitle = !isRtl
    ? app?.result?.onboardingText?.mainInfo?.titleEn
    : app?.result?.onboardingText?.mainInfo?.titleAr;
  const benefitDescription = !isRtl
    ? app?.result?.onboardingText?.mainInfo?.descriptionEn
    : app?.result?.onboardingText?.mainInfo?.descriptionAr;
  const benefitsList = app?.result?.onboardingText?.benefits ?? [];

  const appLocation = app?.result?.uRls?.appSiteUrl;
  const customerService = app?.result?.uRls?.appSupportUrl;
  const appStatus = app?.result?.status;

  // -----------------------------------
  const navigate = useNavigate();

  return (
    <Container>
      <TitleContainer $isRtl={isRtl}>
        <MainTitle>{t("AppMarket.applicationDetails")}</MainTitle>
        <SubTitle>{t("AppMarket.allYouNeed")}</SubTitle>
      </TitleContainer>
      <MainSection>
        <>
          {isLoading ? (
            <AppHeaderSkeleton isRtl={isRtl} />
          ) : (
            <AppHeaderBox>
              <AppLogo $isRtl={isRtl} $active={appStatus === 3} src={logoUrl} />
              <AppInfoContainer>
                <AppName>{name}</AppName>
                <AppDescription>{description}</AppDescription>
                <ButtonContainer>
                  <PrimaryButton
                    $active={appStatus === 3}
                    $isRtl={isRtl}
                    onClick={() => {
                      appStatus !== 3
                        ? navigate(`/apps-MarketPlace/${id}`)
                        : undefined;
                    }}
                  >
                    {t(
                      `AppMarket.${appStatus === 1 ? "activateNow" : statusValue[appStatus as StatusKey]}`
                    )}
                  </PrimaryButton>
                  <SecondaryButton
                    onClick={() => window.open(appLocation, "_blank")}
                    $isRtl={isRtl}
                  >
                    {t("AppMarket.applicationLocation")}
                  </SecondaryButton>
                  <CustomerServiceLink
                    onClick={() => window.open(customerService, "_blank")}
                    $isRtl={isRtl}
                  >
                    {t("AppMarket.customerService")}
                    <ArrowIcon />
                  </CustomerServiceLink>
                </ButtonContainer>
              </AppInfoContainer>
            </AppHeaderBox>
          )}
        </>

        {isLoading ? (
          <BenefitsSectionSkeleton isRtl={isRtl} />
        ) : (
          <BenefitsSection $isRtl={isRtl}>
            <BenefitsTextContainer>
              <BenefitsContent>
                <BenefitsTitle>{benefitTitle}</BenefitsTitle>
                <BenefitsDescription>{benefitDescription}</BenefitsDescription>

                {benefitsList?.map((benefit: any, index: number) => (
                  <div key={benefit?.titleEn}>
                    {index === 0 && <IconWrapper as={Pointer} />}
                    {index === 1 && <IconWrapper as={Communication} />}
                    {index === 2 && <IconWrapper as={IncProfit} />}

                    <BenefitTitle>
                      {isRtl ? benefit?.titleAr : benefit?.titleEn}
                    </BenefitTitle>
                    <BenefitDescription>
                      {isRtl ? benefit?.descriptionAr : benefit?.descriptionEn}
                    </BenefitDescription>
                  </div>
                ))}
              </BenefitsContent>
            </BenefitsTextContainer>
            <BenefitImage src={largLogoUrl} />
          </BenefitsSection>
        )}

        {/* Related Apps */}
        <RelatedAppsBox>
          <RelatedAppsHeader>
            <RelatedAppsTitle>
              {t("AppMarket.otherRecommendedApplications")}
            </RelatedAppsTitle>
            <RelatedAppsSubtitle>
              {t("AppMarket.applicationsWeRecommend")}
            </RelatedAppsSubtitle>
          </RelatedAppsHeader>

          <RelatedAppsGrid>
            {apps?.result?.length > 0 &&
              apps?.result?.map((item: any) => (
                <Card
                  onPress={() => navigate(`/apps-Details/${item.id}`)}
                  status={item.status as number}
                  description={
                    isRtl ? item?.descriptionAr : item?.descriptionEn
                  }
                  title={isRtl ? item?.nameAr : item?.nameEn}
                  icon={item?.logoUrl}
                />
              ))}
          </RelatedAppsGrid>
        </RelatedAppsBox>
      </MainSection>
    </Container>
  );
};

export default AppDetails;
